import { FlagOutlined } from '@ant-design/icons'
import { Checkbox, Divider, Select, Table } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { ColumnProps } from 'antd/lib/table'
import * as H from 'history'
import * as R from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import { compose, withHandlers, withProps } from 'recompose'

import * as notificationActions from '../../actions/notifications'
import { DISPLAY_MODE_TV } from '../../constants/displayMode'
import { isAdmin } from '../../helpers/auth'
import { calculateNextPage } from '../../helpers/category-results'
import { dataFromOrdered, findInOrdered, fsCollections } from '../../helpers/firestore'
import isDefined from '../../helpers/isDefined'
import { categoryResults, playsFinals, resultKey } from '../../helpers/results'
import routes from '../../helpers/routes'
import {
  playerColumns,
  renderScore,
  resultColumns,
  rowParDiffClassName,
} from '../../shared/columns'

import { CategoryId, TournamentCategory } from '../../schemas/Category'
import { Flight } from '../../schemas/Flight'
import { PlayerId, TournamentPlayer } from '../../schemas/Player'
import { Playground } from '../../schemas/Playground'
import { PlayerRoundResult } from '../../schemas/Results'
import { TenantId } from '../../schemas/Tenant'
import { ResultsOptionalColumn, Tournament, TournamentId } from '../../schemas/Tournament'

import './ResultsInCategory.css'

const optionalColumn = (
  { screenResultsOptionalColumns = [], printResultsOptionalColumns = [] }: Tournament,
  key: ResultsOptionalColumn,
) => (columnProps: ColumnProps<FIXME>): ColumnProps<FIXME> | undefined => {
  const screenEnabled = R.includes(key, screenResultsOptionalColumns)
  const printEnabled = R.includes(key, printResultsOptionalColumns)
  const isEnabled = screenEnabled || printEnabled
  const isJustScreen = screenEnabled && !printEnabled
  const isJustPrint = printEnabled && !screenEnabled

  // tslint:disable-next-line:no-console
  console.log({ key, screenEnabled, printEnabled, isJustPrint, isJustScreen })

  const makeClassName = (newClassName: string) =>
    columnProps.className ? `${columnProps.className} ${newClassName}` : newClassName

  if (isEnabled) {
    if (isJustScreen) {
      return {
        ...columnProps,
        className: makeClassName('justScreen'),
      }
    }

    if (isJustPrint) {
      return {
        ...columnProps,
        className: makeClassName('justPrint'),
      }
    }

    return columnProps
  }

  return undefined
}

const getResultsInCategoryColumns = (
  tenant: TenantId,
  tournament: Tournament,
  categoryId: CategoryId,
  admin: boolean,
  setPlayoffFlag: (playerId: PlayerId, round: number) => (playoffFlag: boolean) => void,
  setPlayoffRank: (playerId: PlayerId, round: number) => (playoffRank: number) => void,
) => {
  const getLastRoundPlayoffFlag = R.path<boolean>(['lastRoundPlayoffFlag', categoryId])

  return [
    {
      title: 'rank',
      width: '4em',
      dataIndex: 'rank',
      render: (text, record) => record.totalScore > 0 && text,
    },
    {
      title: 'Player',
      width: '20em',
      key: 'fullName',
      render: (text, record) => (
        <div style={{ textAlign: 'left' }}>
          <Link to={routes.playerResults(tenant, tournament.id, record.id)}>
            {!R.isNil(record.lastPlayedLane) ? '⛳ ' : ''}
            {record.firstName} {record.lastName}
          </Link>
        </div>
      ),
    },
    {
      title: 'LANE',
      width: '2em',
      className: 'ResultsCol--lane',
      render: (text, record) => {
        return record.lastPlayedLane > 0 && record.lastPlayedLane
      },
    },
    optionalColumn(
      tournament,
      ResultsOptionalColumn.Nationality,
    )({
      ...playerColumns.nationality,
      width: '6em',
    }),
    optionalColumn(
      tournament,
      ResultsOptionalColumn.Club,
    )({
      ...playerColumns.club,
      width: '6em',
    }),
    optionalColumn(
      tournament,
      ResultsOptionalColumn.CzechRegistrationId,
    )({
      ...playerColumns.czechRegistrationId,
      width: '6em',
    }),
    ...R.range(0, tournament.rounds).map((index) => ({
      title: `${index + 1}.`,
      width: '2em',
      render: (text, record) => renderScore(record.results[index + 1]),
    })),
    ...(tournament.finalRound
      ? [
          {
            title: <FlagOutlined />,
            width: '2em',
            render: (text, record) => renderScore(record.results[tournament.rounds + 1]),
          },
        ]
      : []),
    resultColumns.sum,
    resultColumns.avg,
    {
      title: 'DIFF',
      dataIndex: 'diff',
      width: '4em',
      className: 'ResultsCol--diff',
      render: (text, record) => (record.lastRound > 1 ? record.diff : null),
    },
    resultColumns.par,
    ...(admin
      ? [
          {
            title: 'Playoff',
            render: (text, record, index) => {
              const playoffFlagOnChangeHandler = (event: CheckboxChangeEvent) => {
                event.preventDefault()
                setPlayoffFlag(record.id, record.lastRound)(event.target.checked)
              }

              return (
                (record.playoffRankPlayers.length > 1 || getLastRoundPlayoffFlag(record)) && (
                  <Checkbox
                    checked={getLastRoundPlayoffFlag(record)}
                    onChange={playoffFlagOnChangeHandler}
                  />
                )
              )
            },
            width: '2em',
          },
          {
            title: 'Rank',
            render: (text, record, index) => {
              const playoffRankChangeHandler = (value: any) => {
                setPlayoffRank(record.id, record.lastRound)(value)
              }

              return (
                getLastRoundPlayoffFlag(record) && (
                  <Select
                    size="small"
                    value={R.path(['lastRoundPlayoffRank', categoryId], record)}
                    onChange={playoffRankChangeHandler}>
                    {R.range(1, record.playoffRankPlayers.length + 1).map((playoffRank) => (
                      <Select.Option key={playoffRank} value={playoffRank}>
                        {playoffRank}
                      </Select.Option>
                    ))}
                  </Select>
                )
              )
            },
            width: '2em',
          },
        ]
      : []),
    {
      title: 'Notes',
      width: '4em',
      render: (text, record, index) => {
        const dnfFlag = record.isDisqualified && 'DNF'
        const penaltyFlag = R.pathOr(0, ['penalties'], record) > 0 && `⚠️ ${record.penalties}`
        const playoffFlag = getLastRoundPlayoffFlag(record) && 'P'

        const flags = [dnfFlag, penaltyFlag, playoffFlag].filter(Boolean)

        return R.intersperse(<Divider type="vertical" />, flags)
      },
    },
  ].filter(isDefined)
}

type ResultsInCategoryOuterProps = {
  tenant: TenantId
  tournamentId: TournamentId
  categoryId: CategoryId
  playground: Playground
  displayMode: string
  cycleTimeout: number
  playersPerPage: number
  onCycleComplete?(): void
}

type ResultsInCategoryInnerProps = ResultsInCategoryOuterProps &
  WithPlayoffHandlers & {
    dataIsLoaded: boolean
    tournament?: Tournament
    category?: TournamentCategory
    isMain: boolean
    tournamentFlights?: Flight[]
    tournamentPlayers?: TournamentPlayer[]
    tournamentResults?: PlayerRoundResult[]
    profile: any // TODO
    location: H.Location
    dataSource: any[] // TODO
    playersInCategory: TournamentPlayer[]
  }

type ResultsInCategoryState = {
  currentPage: number
}

type ResultsInCategoryDefaultProps = {
  onCycleComplete(): void
}

class ResultsInCategory extends React.Component<
  ResultsInCategoryInnerProps & ResultsInCategoryDefaultProps,
  ResultsInCategoryState
> {
  static defaultProps = {
    onCycleComplete: () => ({}), // no-op
  }

  state = {
    currentPage: 1,
  }

  private cycleIntervalId: number

  cyclePages = () => {
    const { dataSource, playersPerPage } = this.props
    const fitsOnePage = dataSource.length <= playersPerPage

    this.setState((state) => {
      const nextPage = calculateNextPage(dataSource.length, playersPerPage, state.currentPage)

      if (fitsOnePage || nextPage < state.currentPage) {
        this.props.onCycleComplete()
      }

      return {
        currentPage: nextPage,
      }
    })
  }

  componentDidMount() {
    if (this.props.displayMode.toLowerCase() === DISPLAY_MODE_TV) {
      this.cycleIntervalId = window.setInterval(this.cyclePages, this.props.cycleTimeout)
    }
  }

  componentWillUnmount() {
    if (this.cycleIntervalId) {
      window.clearInterval(this.cycleIntervalId)
    }
  }

  render() {
    const {
      dataSource,
      profile,
      dataIsLoaded,
      isMain,
      tenant,
      categoryId,
      tournament,
      category,
      playersInCategory,
      tournamentFlights = [],
      setPlayoffFlag,
      setPlayoffRank,
      displayMode,
      playersPerPage,
    } = this.props

    const { currentPage } = this.state

    const isMainWithFinal = isMain && tournament && tournament.finalRound

    // computed to fix display of finals divider in case of DNF in final round
    const dnfFinalsPlayersCount = isMainWithFinal
      ? playersInCategory.filter(
          (player) => player.isDisqualified && playsFinals(player.id, tournamentFlights),
        ).length
      : 0

    const playersWithResultsCount = R.takeWhile((result) => result.totalScore > 0, dataSource)
      .length

    const finalRoundRowClassName = (record: any /* TODO */, index: number) =>
      isMainWithFinal &&
      (currentPage - 1) * playersPerPage + index + 1 ===
        R.min(tournament!.playersInFinalRound! - dnfFinalsPlayersCount, playersWithResultsCount)
        ? 'with-thick-bottom-border'
        : ''

    const rowClassName = (record: any /* TODO */, index: number) =>
      [finalRoundRowClassName(record, index), rowParDiffClassName(record)].join(' ')

    const tableTitle = () => category!['name:en'] // TODO take from locale

    const displayNotes = isMain && tournament && tournament.notes

    const tableFooter = displayNotes ? () => tournament!.notes : undefined

    const tablePagination =
      displayMode === DISPLAY_MODE_TV ? { current: currentPage, pageSize: playersPerPage } : false

    return (
      <Table
        rowKey="id"
        className="ResultsInCategory"
        title={tableTitle}
        loading={!dataIsLoaded}
        bordered
        dataSource={dataSource}
        columns={
          tournament &&
          getResultsInCategoryColumns(
            tenant,
            tournament,
            categoryId,
            isAdmin(tenant, profile),
            setPlayoffFlag,
            setPlayoffRank,
          )
        }
        pagination={tablePagination}
        footer={tableFooter}
        rowClassName={rowClassName}
      />
    )
  }
}

type WithPlayoffHandlers = {
  setPlayoffFlag(playerId: PlayerId, round: number): (playoffFlag: boolean) => Promise<any>
  setPlayoffRank(playerId: PlayerId, round: number): (playoffRank: number) => Promise<any>
}

const emptyResult = (playerId: PlayerId, round: number): PlayerRoundResult => ({
  id: resultKey(playerId, round),
  playerId,
  round,
  isFinal: false,
  results: R.repeat(0, 18) as FIXME,
})

const mapStateToProps = (
  { firestore, firebase: { profile } },
  { tenant, tournamentId, categoryId },
) => ({
  tournament: findInOrdered(firestore.ordered[fsCollections.tournaments(tenant)], tournamentId),
  category: findInOrdered(
    firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    categoryId,
  ),
  tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
  tournamentPlayersMap: dataFromOrdered(
    firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
  ),
  tournamentFlights: firestore.ordered[fsCollections.tournamentFlights(tenant, tournamentId)],
  tournamentResultsMap: dataFromOrdered(
    firestore.ordered[fsCollections.tournamentResults(tenant, tournamentId)],
  ),
  tournamentCombinedResults:
    firestore.ordered[fsCollections.tournamentCombinedResults(tenant, tournamentId)],
  profile,
})

const createWatchArray = ({ tenant, tournamentId }) => [
  { collection: fsCollections.tournaments(tenant), doc: tournamentId },
  { collection: fsCollections.tournamentCategories(tenant, tournamentId) },
  { collection: fsCollections.tournamentFlights(tenant, tournamentId) },
  { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
  { collection: fsCollections.tournamentResults(tenant, tournamentId) },
  { collection: fsCollections.tournamentCombinedResults(tenant, tournamentId) },
]

const addDataIsLoadedFlag = ({
  tournament,
  category,
  tournamentPlayers,
  tournamentPlayersMap,
  tournamentFlights,
  tournamentCombinedResults,
  tournamentResultsMap,
}) => ({
  dataIsLoaded: isLoaded(
    tournament,
    category,
    tournamentPlayers,
    tournamentPlayersMap,
    tournamentFlights,
    tournamentCombinedResults,
    tournamentResultsMap,
  ),
})

// adds props related to `Category`
const addCategoryProps = ({ categoryId, category }) => ({
  category: category && {
    ...category,
    id: categoryId,
  },
  isMain: category && category.main,
})

const addDataSource = ({
  dataIsLoaded,
  tournament,
  category,
  categoryId,
  tournamentFlights,
  tournamentCombinedResults,
  tournamentPlayers,
  tournamentPlayersMap,
}) => {
  const playersInCategory = dataIsLoaded
    ? R.filter(
        (player: FIXME) => R.contains(categoryId, player.categories) as FIXME,
        tournamentPlayers,
      )
    : []
  const playersInCategoryIds = playersInCategory.map(R.prop('id'))
  const dataSource = dataIsLoaded
    ? categoryResults(
        tournament,
        category,
        tournamentFlights,
        tournamentCombinedResults.filter((result) => R.contains(result.id, playersInCategoryIds)),
        tournamentPlayersMap,
      )
    : []

  return {
    dataSource,
    playersInCategory,
  }
}

export default compose<ResultsInCategoryInnerProps, ResultsInCategoryOuterProps>(
  withFirebase,
  withFirestore,
  withRouter,
  firestoreConnect(createWatchArray),
  connect(mapStateToProps),
  withProps(addDataIsLoadedFlag),
  withProps(addCategoryProps),
  withProps(addDataSource),
  withHandlers({
    onSetPlayoffFlagError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff flag'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    onSetPlayoffRankError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff rank'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    saveResult: ({ firestore, tenant, tournamentId }) => (playerId: PlayerId, round: number) => (
      result: any /* TODO */,
    ) =>
      firestore.set(
        {
          collection: fsCollections.tournamentResults(tenant, tournamentId),
          doc: resultKey(playerId, round),
        },
        result,
      ),
  }),
  withHandlers<FIXME, WithPlayoffHandlers>({
    setPlayoffFlag: ({ saveResult, categoryId, tournamentResultsMap, onSetPlayoffFlagError }) => (
      playerId: PlayerId,
      round: number,
    ) => (playoffFlag: boolean) => {
      const key = resultKey(playerId, round)
      const result = tournamentResultsMap[key] || emptyResult(playerId, round)

      return saveResult(
        playerId,
        round,
      )(
        (R.compose(
          R.set(R.lensPath(['playoffRank', categoryId]), playoffFlag ? 1 : null),
          R.set(R.lensPath(['playoffFlag', categoryId]), playoffFlag) as FIXME,
        ) as FIXME)(result),
      ).catch(onSetPlayoffFlagError)
    },
    setPlayoffRank: ({ saveResult, categoryId, tournamentResultsMap, onSetPlayoffRankError }) => (
      playerId: PlayerId,
      round: number,
    ) => (rank: number) => {
      const key = resultKey(playerId, round)
      const result = tournamentResultsMap[key] || emptyResult(playerId, round)

      return saveResult(
        playerId,
        round,
      )(
        (R.compose(
          R.set(R.lensPath(['playoffRank', categoryId]), rank),
          R.set(R.lensPath(['playoffFlag', categoryId]), true) as FIXME,
        ) as FIXME)(result),
      ).catch(onSetPlayoffRankError)
    },
  }),
)(ResultsInCategory)



// WEBPACK FOOTER //
// ./src/components/tournament/ResultsInCategory.tsx